import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SHA256 } from 'crypto-es/lib/sha256';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '@environments/environment';

import { LanguageService } from './language.service';
import { UiService } from './ui.service';


import { AppRights } from '@classes/app-rights';
import { AuthData } from '@classes/auth/auth-data';
import { AuthResponse } from '@classes/auth/auth-response';
import { AuthUser } from '@classes/auth/auth-user';
import { Token } from '@classes/token';
import { ReducedProfile } from '@interfaces/profiles/reduced-profile';
import { Role } from '@interfaces/profiles/role';
import { RoleKeys } from '../models/enums/profile/roles';
import { AppRightsService } from './app-rights.service';
import { ReferenceMonthService } from './reference-month.service';
import { RoleService } from './role.service';
import { SessionStorageService } from './session-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    authChanged = new BehaviorSubject<boolean>(false);
    private authUserChangedSubject = new BehaviorSubject<AuthUser>(undefined);
    private rolesSubject = new BehaviorSubject<string[]>(undefined)

    private identityApiUrl: string = environment.identityApiUrl;
    private apiUrl: string = environment.apiUrl;
    private tokenKey: string = environment.tokenKey;
    private rfTokenKey: string = environment.rfTokenKey;
    private tokenExpKey: string = environment.tokenExpKey;

    // private authUser: AuthUser;

    public redirectUrl: string;

    constructor(
        private router: Router,
        private http: HttpClient,
        private jwtHelperSrv: JwtHelperService,
        private languageSrv: LanguageService,
        private refMonthSrv: ReferenceMonthService,
        private uiSrv: UiService,
        private roleSrv: RoleService,
        private appRightsSrv: AppRightsService,
        private sessionStorageSrv: SessionStorageService,
        private zone: NgZone
    ) { }

    get authUser(): AuthUser {
        return this.authUserChangedSubject.getValue();
    }

    get authUser$(): Observable<AuthUser> {
        return this.authUserChangedSubject.asObservable();
    }

    set authUser(authUser: AuthUser) {
        this.authUserChangedSubject.next(authUser);
    }

    get roles$(): Observable<string[]> {
        return this.rolesSubject.asObservable();
    }

    get roles(): string[] {
        return this.rolesSubject.getValue();
    }

    set roles(roles: Role[]) {
        this.rolesSubject.next(roles.map(r => r.id.toString()));
    }

    login(authData: AuthData): Observable<AuthResponse> {
        const hashedPassword = this.cryptString(authData.password);

        const httpParams = new HttpParams()
            .append('username', authData.username)
            .append('password', hashedPassword)
            .append('grant_type', 'password')
            .append('scope', 'api offline_access')
            .append('client_id', 'web');

        return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
            .pipe(
                tap(result => {
                    this.saveToken(result.access_token, result.refresh_token, result.expires_in);
                    this.languageSrv.setCurrentLanguage();

                    if (!sessionStorage.getItem('current-profile')) {
                        this.router.navigate(['/select-profile']);
                    } else {
                        if (this.redirectUrl) {
                            this.router.navigate([this.redirectUrl]);
                            this.redirectUrl = null;
                        } else {
                            this.router.navigate(['/home']);
                        }
                    }

                })
            );
    }

    updatePassword(resetData: { currentPassword: string, newPassword: string }): Observable<string> {
        const hashedCurrentPassword = this.cryptString(resetData.currentPassword);
        const hashedNewPassword = this.cryptString(resetData.newPassword);

        const httpParams = new HttpParams()
            .append('current_password', hashedCurrentPassword)
            .append('new_password', hashedNewPassword);

        return this.http.post<string>(this.apiUrl + 'users/password', httpParams)
            .pipe(
                tap(() => {
                    this.afterPwdReset(resetData.newPassword);
                })
            );
    }

    requestPasswordReset(username: string) {
        const httpParams = new HttpParams().append('username', username);

        return this.http.post(this.apiUrl + 'user/password-reset-request', httpParams);
    }

    requestPasswordInit(username: string) {
        const httpParams = new HttpParams()
            .append('username', username);

        return this.http.post(this.apiUrl + 'user/password-init-request', httpParams);
    }

    /**
     * Resets the user's password using a token and a new password.
     * @param userId The ID of the user whose password is being reset.
     * @param token The token received for password reset.
     * @param newPassword The new password to set.
     * @returns {Observable<any>} An observable that completes when the password is reset.
     */
    resetPassword(userId: string, token: string, newPassword: string) {
        const hashedNewPassword = this.cryptString(newPassword);

        // Send data in body in place of http params to avoid mess in token serialisation due to special characters
        const body = {
            token,
            new_password: hashedNewPassword
        };

        return this.http.post(`${this.apiUrl}user/${userId}/password-reset`, body)
            .pipe(
                tap(() => {
                    this.router.navigate(['/login']);
                })
            );
    }

    /**
     * Refreshes the access token using the refresh token.
     * @returns {Observable<AuthResponse>} The refreshed auth response.
     */
    refreshToken(): Observable<AuthResponse> {
        const refreshToken = localStorage.getItem(this.rfTokenKey);

        const httpParams = new HttpParams()
            .append('grant_type', 'refresh_token')
            .append('scope', 'api offline_access')
            .append('client_id', 'web')
            .append('refresh_token', refreshToken);

        return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
            .pipe(
                tap(result => {
                    // this.refMonthSrv.getRefMonth().subscribe();
                    // this.refMonthSrv.getLastNormalized().subscribe();
                    this.saveToken(result.access_token, result.refresh_token, result.expires_in);
                })
            );
    }


    logout(): void {
        localStorage.clear();
        sessionStorage.clear();
        this.authChanged.next(false);
        this.authUser = null;
        this.refMonthSrv.refMonthSubject.next(null);
        this.refMonthSrv.lastNormalizedMonthSubject.next(null);
        this.zone.run(() => this.router.navigate(['/login']));
    }

    /**
     * Checks if the user is authenticated by verifying the token and checking if a profile is set in session storage.
     * @returns {boolean} True if the user is authenticated, false otherwise.
     */
    isAuth(): boolean {
        const decodedToken = this.getDecodedToken();

        if (!decodedToken) {
            return false;
        }

        if (this.isTokenExpired() === false /*&& !!sessionStorage.getItem('current-profile') === true*/) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Retrieves the decoded JWT token.
     * @returns {Token} The decoded token or null if no token is found.
     */
    getDecodedToken(): Token {
        const token = this.jwtHelperSrv.tokenGetter() as string;

        if (token) {
            return this.jwtHelperSrv.decodeToken(token);
        }

        return null;
    }

    /**
     * Sets the authenticated user based on the decoded token and the current profile stored in session storage.
     * If no profile is found, it emits false to authChanged.
     */
    setAuthUser(): void {
        const decodedToken = this.getDecodedToken();
        if (!decodedToken) {
            return null;
        }

        const profile = JSON.parse(sessionStorage.getItem('current-profile')) as ReducedProfile;
        if (profile) {
            const client = profile.client;
            const authUser = new AuthUser(decodedToken.name, profile.roles, client, decodedToken.email, profile.name, decodedToken.language);
            this.appRightsSrv.appRights = new AppRights(authUser.roles);
            this.authUser = authUser;
            this.authChanged.next(true);
        } else {
            this.authChanged.next(false);
        }
    }

    /**
     * Retrieves the authenticated user. If the user is not set, it calls setAuthUser to initialize it.
     * @returns {AuthUser} The authenticated user.
     */
    getAuthUser(): AuthUser {
        if (!this.authUser) {
            this.setAuthUser();
        }

        return this.authUser;
    }

    /**
     * Checks if the user is authorized based on their roles and clients.
     * @param roles The required roles.
     * @param clients The allowed clients.
     * @returns {boolean} True if the user is authorized, false otherwise.
     */
    isAuthorized(roles: RoleKeys[], clients?: string[]): boolean {
        const token = this.jwtHelperSrv.tokenGetter();

        if (clients?.length) {
            if (clients.some(client => this.getAuthUser().client === client)) {
                if (!roles) {
                    return true;
                }

                if (!token) {
                    return false;
                }

                return this.hasRoles(roles);
            }
        } else {
            if (!roles) {
                return true;
            }

            if (!token) {
                return false;
            }
            return this.hasRoles(roles);
        }

        // role not authorised
        return false;
    }

    /**
     * Retrieves the JWT token.
     * @returns {string} The JWT token.
     */
    getToken(): string {
        return this.jwtHelperSrv.tokenGetter() as string;
    }

    /**
     * Sets the current profile and retrieves its roles from the server.
     * If successful, it updates the session storage and navigates to the home page.
     * @param profile The reduced profile to set as current.
     */
    setCurrentProfile(profile: ReducedProfile): void {
        this.roleSrv.getProfileRoles(profile.id)
            .subscribe({
                next: (rolesFromSrv: Role[]) => {
                    profile.roles = rolesFromSrv.map(r => r.id.toString()) as RoleKeys[];
                    sessionStorage.setItem('current-profile', JSON.stringify(profile));
                    this.setAuthUser();
                    this.router.navigate(['/home']);
                }
            });
    }

    /**
     * Saves the access token, refresh token, and expiration date in local storage.
     * @param accessToken The access token to save.
     * @param refreshToken The refresh token to save.
     * @param deadline The expiration time in seconds.
     */
    private saveToken(accessToken: string, refreshToken: string, deadline: number): void {
        const tokenExpDate = new Date();
        tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

        localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.rfTokenKey, refreshToken);
    }

    /**
     * Handles actions after a password reset.
     * @param newPassword The new password to set.
     */
    private afterPwdReset(newPassword: string): void {
        const decodedToken = this.getDecodedToken();

        if (decodedToken) {
            this.login({ username: decodedToken.name, password: newPassword });
        } else {
            this.logout();
        }
    }

    /**
     * Checks if the token is expired by comparing the expiration date with the current date.
     * @returns {boolean} True if the token is expired, false otherwise.
     */
    private isTokenExpired(): boolean {
        const tokenExpDate = new Date(localStorage.getItem(this.tokenExpKey));
        const now = new Date();
        if (tokenExpDate < now) {
            this.refreshToken().subscribe({
                next: (result: AuthResponse) => {   
                    return false;
                }
            });
        }
        return false;
    }

    /**
     * Encrypts a string using SHA256.
     * @param stringTocrypt The string to encrypt.
     * @returns {string} The encrypted string.
     */
    private cryptString(stringTocrypt: string): string {
        return SHA256(stringTocrypt).toString();
    }

    /**
     * Checks if the user has the required roles.
     * @param roles The required roles.
     * @returns {boolean} True if the user has at least one of the required roles, false otherwise.
     */
    private hasRoles(roles: RoleKeys | RoleKeys[]): boolean {
        if (this.getAuthUser().roles?.length) {
            if (typeof roles === 'string') {
                return this.authUser.roles.find(r => r === roles) ? true : false;
            } else {
                return roles.some(role => this.authUser.roles.find(r => r === role));
            }
        } else {
            this.uiSrv.showSnackbar('auth.noRoles.summary', true);
            this.logout();
            return false;
        }
    }

}
